import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import FavouriteButton from '../FavouriteButton/FavouriteButton';
import { getPlanConfig, shouldShowVerifiedBadge } from '../../utils/membershipUtils';
import { LOGO_PLACEHOLDER } from '../../utils/placeholders';

export default function MarketplaceProductCard({
  product,
  addedProducts,
  isInInquiry,
  onInquire,
  variant = 'default',
}) {
  const navigate = useNavigate();
  const { formatPriceRange } = useCurrency();
  const seller = product.seller || {};
  const displayCompanyName = seller.name || 'Verified supplier';
  const displayLogo = seller.logo || LOGO_PLACEHOLDER;
  const isDeal = variant === 'deal';

  const handleCardClick = (e) => {
    if (e.target.closest('button')) return;
    navigate(`/product/${product.id}`);
  };

  const planConfig = getPlanConfig(seller.membershipPlan);

  /* Compute a fake strikethrough price for deal variant */
  const fakeOldMin = isDeal ? ((parseFloat(product.priceMin) || 0) * 1.09).toFixed(2) : null;
  const fakeOldMax = isDeal ? ((parseFloat(product.priceMax) || parseFloat(product.priceMin) || 0) * 1.13).toFixed(2) : null;

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => e.key === 'Enter' && handleCardClick(e)}
      className="mh-product-card mh-alibaba-product-card group relative flex flex-col rounded-xl bg-white border border-[#ebebeb] overflow-hidden transition-all duration-300 min-w-[190px] w-[210px] max-w-[230px] shrink-0 snap-start"
    >
      {/* Product image */}
      <div className="mh-pc-img-wrap relative overflow-hidden bg-gray-50" style={{ aspectRatio: '1' }}>
        <img
          src={product.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
        />
        {/* Favourite button */}
        <div className="absolute top-2 right-2">
          <FavouriteButton productId={product.id} />
        </div>
        {/* Deal badge */}
        {isDeal && (
          <span
            className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide text-white px-2 py-0.5 rounded"
            style={{ background: 'linear-gradient(90deg,#d9261c,#e84118)' }}
          >
            Deal
          </span>
        )}
      </div>

      {/* Card body */}
      <div className="mh-pc-body flex flex-col flex-1" style={{ padding: '10px 12px 12px', gap: 4 }}>
        {/* Product name */}
        <h3
          className="text-[13px] font-semibold leading-snug line-clamp-2 group-hover:text-[#ff6a00] transition-colors"
          style={{ color: '#1b1b1b', margin: 0 }}
        >
          {product.name || 'Product'}
        </h3>

        {/* Price */}
        {isDeal ? (
          <div style={{ marginTop: 4 }}>
            <div className="flex items-center gap-1">
              {/* Down arrow price indicator */}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <path d="M12 5v14M5 12l7 7 7-7" stroke="#d9261c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#d9261c' }}>
                {formatPriceRange(product.priceMin, product.priceMax)}
              </span>
            </div>
            {fakeOldMin && (
              <p style={{ fontSize: 11, color: '#aaa', textDecoration: 'line-through', margin: '1px 0 0' }}>
                {formatPriceRange(fakeOldMin, fakeOldMax)}
              </p>
            )}
          </div>
        ) : (
          <p style={{ fontSize: 15, fontWeight: 700, color: '#1b1b1b', margin: '4px 0 0' }}>
            {formatPriceRange(product.priceMin, product.priceMax)}
          </p>
        )}

        {/* MOQ */}
        <p style={{ fontSize: 12, color: '#666', margin: 0 }}>
          <span style={{ fontWeight: 600, color: '#444' }}>MOQ:</span> {product.moq || '1'} · {product.unit || 'unit'}
        </p>

        {/* Supplier badges */}
        <div className="flex flex-wrap gap-1.5 items-center" style={{ marginTop: 'auto', paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border"
            style={{ borderColor: planConfig.color, color: planConfig.color }}
          >
            {planConfig.icon && <img src={planConfig.icon} alt="" style={{ width: 14, height: 14 }} />}
            {planConfig.label}
          </span>
          {shouldShowVerifiedBadge(seller.membershipPlan) && (
            <span className="text-[10px] font-semibold text-sky-600 bg-sky-50 px-2 py-0.5 rounded-full">
              Verified
            </span>
          )}
        </div>

        {/* Seller logo + name */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (seller.id) navigate(`/seller/${seller.id}`);
          }}
          className="flex items-center gap-2 text-xs text-gray-700 hover:text-[#ff6a00] transition-colors text-left"
          style={{ marginTop: 4 }}
        >
          <img
            src={displayLogo}
            alt=""
            style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', border: '1px solid #eee', flexShrink: 0 }}
          />
          <span className="truncate font-medium">{displayCompanyName}</span>
        </button>

        {/* Country + years info */}
        <div className="flex items-center gap-1.5 text-[11px] text-gray-500" style={{ marginTop: 2 }}>
          <img
            src={seller.countryFlag || 'https://flagcdn.com/w40/ae.png'}
            alt=""
            style={{ width: 16, height: 12, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }}
          />
          <span>
            {(seller.country || 'UAE').replace('United Arab Emirates', 'UAE')} · {seller.memberYears || 1}+ yrs
          </span>
          <span style={{ color: '#ccc' }}>·</span>
          <span className="truncate">{seller.businessType || 'Manufacturer'}</span>
        </div>

        {/* Inquire button */}
        <button
          type="button"
          onClick={(e) => onInquire(product, e)}
          style={{
            marginTop: 8,
            width: '100%',
            borderRadius: 999,
            padding: '7px 0',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            border: isInInquiry(product.id) ? '1px solid #e5e7eb' : 'none',
            background: isInInquiry(product.id)
              ? '#f3f4f6'
              : 'linear-gradient(90deg, #ff6a00, #e85a00)',
            color: isInInquiry(product.id) ? '#374151' : '#fff',
          }}
        >
          {addedProducts[product.id] === true
            ? '✓ Added!'
            : isInInquiry(product.id)
            ? 'Remove from inquiry'
            : 'Inquire now'}
        </button>
      </div>
    </article>
  );
}
